import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addNotification } from '../../store/slices/uiSlice';
import { adminApiService } from '../../services/adminApi';
import TitheReceiptModal from './TitheReceiptModal';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/date';
import useExcelBatch, { ExcelBatchButtons, ExcelImportConfirmModal } from '../../hooks/useExcelBatch';
import actionBtn from '../common/actionButtonStyles';

const TitheReceiptDataTable = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [titheReceipts, setTitheReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Statistics state
  const [statistics, setStatistics] = useState(null);

  const batch = useExcelBatch({
    exportUrl: '/api/v1/tithe-receipts/export',
    templateUrl: '/api/v1/tithe-receipts/import-template',
    importUrl: '/api/v1/tithe-receipts/import',
    templateFilename: 'tithe_receipt_import_template.xlsx',
    exportFilenamePrefix: 'tithe_receipts_export',
    exportParams: {
      status_filter: statusFilter || undefined,
    },
    upsertKey: 'id',
    getExistingKeys: async () => {
      const response = await adminApiService.titheReceipts.getAll({ limit: 99999 });
      const rows = response.data || response || [];
      return rows.map((r) => String(r.id));
    },
  });

  useEffect(() => {
    fetchTitheReceipts();
    fetchStatistics();
  }, [statusFilter, yearFilter, monthFilter]);

  const fetchTitheReceipts = async () => {
    try {
      setLoading(true);
      const params = {
        skip: 0,
        limit: 1000 // Get all for client-side filtering
      };
      
      if (statusFilter) {
        params.status_filter = statusFilter;
      }

      const response = await adminApiService.titheReceipts.getAll(params);
      setTitheReceipts(response.data || response);
      setError(null);
    } catch (error) {
      console.error('Error fetching tithe receipts:', error);
      setError(error.response?.data?.detail || t('tithe.notifications.fetchError'));
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: t('tithe.notifications.fetchError')
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await adminApiService.titheReceipts.getStatistics();
      setStatistics(response.data || response);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: t('tithe.notifications.statsError')
      }));
    }
  };

  // Filter receipts based on search term and date filters
  const filteredReceipts = titheReceipts.filter(receipt => {
    // Search filter
    const matchesSearch = receipt?.chinese_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      receipt?.contact_phone?.includes(searchTerm) ||
      receipt?.worship_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      receipt?.receiving_bank?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      receipt?.transaction_type?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Year filter
    const receiptYear = new Date(receipt.created_at).getFullYear().toString();
    const matchesYear = !yearFilter || receiptYear === yearFilter;
    
    // Month filter
    const receiptMonth = (new Date(receipt.created_at).getMonth() + 1).toString().padStart(2, '0');
    const matchesMonth = !monthFilter || receiptMonth === monthFilter;
    
    return matchesSearch && matchesYear && matchesMonth;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredReceipts.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentReceipts = filteredReceipts.slice(startIndex, endIndex);
  
  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Format currency
  const formatCurrency = (amount) => {
    return `HK$${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle status update
  const handleStatusUpdate = async (receiptId, newStatus, remarks = '') => {
    try {
      await adminApiService.titheReceipts.updateStatus(receiptId, {
        status: newStatus,
        remarks: remarks
      });

      dispatch(addNotification({
        type: 'success',
        title: 'Status Updated',
        message: t('tithe.notifications.statusUpdated', {
          status: t(`tithe.table.statusLabel.${newStatus}`, newStatus)
        })
      }));

      // Refresh data
      fetchTitheReceipts();
      fetchStatistics();
    } catch (error) {
      console.error('Error updating status:', error);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.detail || t('tithe.notifications.statusError')
      }));
    }
  };

  // Handle view receipt
  const handleViewReceipt = async (receipt) => {
    try {
      // Fetch detailed receipt data
      const response = await adminApiService.titheReceipts.getById(receipt.id);
      setSelectedReceipt(response.data || response);
      setShowReceiptModal(true);
    } catch (error) {
      console.error('Error fetching receipt details:', error);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: t('tithe.notifications.receiptError')
      }));
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: t('tithe.notifications.receiptError')
      }));
    }
  };

  // Handle download receipt file
  const handleDownloadReceipt = async (receiptId, fileName) => {
    try {
      const response = await adminApiService.titheReceipts.downloadReceipt(receiptId);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || `receipt_${receiptId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      dispatch(addNotification({
        type: 'success',
        title: 'Download Started',
        message: t('tithe.notifications.downloadStarted')
      }));
    } catch (error) {
      console.error('Error downloading receipt:', error);
      dispatch(addNotification({
        type: 'error',
        title: 'Download Error',
        message: t('tithe.notifications.downloadError')
      }));
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Security Notice */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-green-800 text-sm font-medium">
            🔒 {t('tithe.secureNotice')}
          </span>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="space-y-4">
          {/* This Month's Statistics */}
          {statistics.monthly && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-3">
                📅 {t('tithe.stats.monthlyTitle', { month: statistics.monthly.current_month })}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-blue-500">
                  <div className="flex items-center">
                    <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs font-medium text-gray-500">{t('tithe.stats.receipts')}</p>
                      <p className="text-xl font-bold text-gray-900">{statistics.monthly.total_receipts}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-yellow-500">
                  <div className="flex items-center">
                    <div className="p-2 rounded-full bg-yellow-100 text-yellow-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs font-medium text-gray-500">{t('tithe.stats.pending')}</p>
                      <p className="text-xl font-bold text-gray-900">{statistics.monthly.pending_receipts}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-green-500">
                  <div className="flex items-center">
                    <div className="p-2 rounded-full bg-green-100 text-green-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs font-medium text-gray-500">{t('tithe.stats.approved')}</p>
                      <p className="text-xl font-bold text-gray-900">{statistics.monthly.approved_receipts}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-purple-500">
                  <div className="flex items-center">
                    <div className="p-2 rounded-full bg-purple-100 text-purple-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs font-medium text-gray-500">{t('tithe.stats.totalAmount')}</p>
                      <p className="text-xl font-bold text-gray-900">{formatCurrency(statistics.monthly.donation_totals?.grand_total)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* All-Time Statistics */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">
              📊 {t('tithe.stats.allTime')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow-md p-5">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">{t('tithe.stats.receipts')}</p>
                    <p className="text-xl font-bold text-gray-900">{statistics.total_receipts}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-5">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-yellow-100 text-yellow-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">{t('tithe.stats.pending')}</p>
                    <p className="text-xl font-bold text-gray-900">{statistics.pending_receipts}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-5">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-green-100 text-green-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">{t('tithe.stats.approved')}</p>
                    <p className="text-xl font-bold text-gray-900">{statistics.approved_receipts}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-5">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-purple-100 text-purple-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">{t('tithe.stats.totalAmount')}</p>
                    <p className="text-xl font-bold text-gray-900">{formatCurrency(statistics.donation_totals?.grand_total)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
          <h2 className="text-xl font-semibold text-gray-900">{t('tithe.title')}</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <ExcelBatchButtons batch={batch} />
            <button
              onClick={fetchTitheReceipts}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              disabled={loading}
            >
              {t('tithe.refresh')}
            </button>
          </div>
        </div>

        <ExcelImportConfirmModal
          batch={batch}
          onConfirm={() => {
            fetchTitheReceipts();
            fetchStatistics();
          }}
        />
        
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder={t('tithe.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('tithe.filters.statusAll')}</option>
            <option value="pending">{t('tithe.filters.statusPending')}</option>
            <option value="approved">{t('tithe.filters.statusApproved')}</option>
            <option value="rejected">{t('tithe.filters.statusRejected')}</option>
          </select>
          
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('tithe.filters.yearAll')}</option>
            <option value="2025">2025</option>
            <option value="2024">2024</option>
            <option value="2023">2023</option>
          </select>
          
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('tithe.filters.monthAll')}</option>
            <option value="01">{t('tithe.filters.month01')}</option>
            <option value="02">{t('tithe.filters.month02')}</option>
            <option value="03">{t('tithe.filters.month03')}</option>
            <option value="04">{t('tithe.filters.month04')}</option>
            <option value="05">{t('tithe.filters.month05')}</option>
            <option value="06">{t('tithe.filters.month06')}</option>
            <option value="07">{t('tithe.filters.month07')}</option>
            <option value="08">{t('tithe.filters.month08')}</option>
            <option value="09">{t('tithe.filters.month09')}</option>
            <option value="10">{t('tithe.filters.month10')}</option>
            <option value="11">{t('tithe.filters.month11')}</option>
            <option value="12">{t('tithe.filters.month12')}</option>
          </select>
          
          <select
            value={pageSize}
            onChange={(e) => setPageSize(parseInt(e.target.value))}
            className="pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={10}>{t('tithe.filters.perPage', { count: 10 })}</option>
            <option value={25}>{t('tithe.filters.perPage', { count: 25 })}</option>
            <option value={50}>{t('tithe.filters.perPage', { count: 50 })}</option>
            <option value={100}>{t('tithe.filters.perPage', { count: 100 })}</option>
          </select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('tithe.table.receiptInfo')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('tithe.table.contact')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('tithe.table.payment')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('tithe.table.amount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('tithe.table.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('tithe.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentReceipts.map((receipt) => (
                <tr key={receipt.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          #{receipt.id} - {receipt.chinese_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {t('tithe.table.worship')} {receipt.worship_code || t('common.na')}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDateTime(receipt.created_at)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{receipt.contact_phone}</div>
                    <div className="text-sm text-gray-500">{receipt.email || t('tithe.table.emailMissing')}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{receipt.receiving_bank}</div>
                    <div className="text-sm text-gray-500">{receipt.transaction_type}</div>
                    <div className="text-sm text-gray-500">
                      {formatDateTime(receipt.deposit_date)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(receipt.total_amount)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {receipt.tithe_amount > 0 && `${t('tithe.table.breakdown.tithe')}: ${formatCurrency(receipt.tithe_amount)}`}
                      {receipt.building_fund > 0 && ` ${t('tithe.table.breakdown.building')}: ${formatCurrency(receipt.building_fund)}`}
                      {receipt.thanksgiving_offering > 0 && ` ${t('tithe.table.breakdown.thanksgiving')}: ${formatCurrency(receipt.thanksgiving_offering)}`}
                      {receipt.love_neighbor_fund > 0 && ` ${t('tithe.table.breakdown.loveFund')}: ${formatCurrency(receipt.love_neighbor_fund)}`}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(receipt.status)}`}>
                      {t(`tithe.table.statusLabel.${receipt.status}`, receipt.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewReceipt(receipt)}
                        className={actionBtn.view}
                      >
                        {t('tithe.table.view')}
                      </button>
                      
                      {receipt.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleStatusUpdate(receipt.id, 'approved')}
                            className={actionBtn.approve}
                          >
                            {t('tithe.table.approve')}
                          </button>
                          <button
                            onClick={() => handleStatusUpdate(receipt.id, 'rejected')}
                            className={actionBtn.reject}
                          >
                            {t('tithe.table.reject')}
                          </button>
                        </>
                      )}
                      
                      {receipt.receipt_file_name && (
                        <button
                          onClick={() => handleDownloadReceipt(receipt.id, receipt.receipt_file_name)}
                          className={actionBtn.download}
                        >
                          {t('tithe.table.download')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t">
            <div className="flex items-center text-sm text-gray-500">
              {t('tithe.pagination.showing', { from: startIndex + 1, to: Math.min(endIndex, filteredReceipts.length), total: filteredReceipts.length })}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                {t('tithe.pagination.previous')}
              </button>
              
              <span className="px-3 py-1 text-sm">
                {t('tithe.pagination.pageOf', { page: currentPage, total: totalPages })}
              </span>
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                {t('tithe.pagination.next')}
              </button>
            </div>
          </div>
        )}
        
        {filteredReceipts.length === 0 && !loading && (
          <div className="text-center py-12 text-gray-500">
            <div className="text-6xl mb-4">📄</div>
            <p>{t('tithe.empty.none')}</p>
            {searchTerm && <p className="text-sm">{t('tithe.empty.adjust')}</p>}
          </div>
        )}
      </div>

      {/* Receipt Modal */}
      {showReceiptModal && selectedReceipt && (
        <TitheReceiptModal
          receipt={selectedReceipt}
          onClose={() => {
            setShowReceiptModal(false);
            setSelectedReceipt(null);
          }}
          onStatusUpdate={handleStatusUpdate}
        />
      )}
    </div>
  );
};

export default TitheReceiptDataTable;
